// Landing pages · editor por bloques (JSON en `design`) con vista previa escritorio/móvil.
// La vista previa la renderiza el servidor (mismo HTML y CSP que la página pública).
import type { JSX, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlignCenter, AlignLeft, AlignRight, ArrowDown, ArrowLeft, ArrowUp, Clock, Code, Columns3, Copy, FileText, Image as ImageIcon, LayoutTemplate,
  Minus, Monitor, MousePointerClick, PanelBottom, Plus, RefreshCw, Save, Share2, Smartphone, Trash2, Type, Users, Video,
} from "lucide-react";
import { api, ApiError } from "../../../lib/api.ts";
import { Button, Card, ErrorState, Field, Input, Loading, Select, Textarea } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import { MediaPicker } from "../shared/MediaPicker.tsx";
import { type Block, type BlockType, type Design, FONTS, type LandingDetailData, type Seo, newId } from "./shared.tsx";

const BLOCKS: Array<{ type: BlockType; label: string; icon: typeof Type }> = [
  { type: "hero", label: "Banner / Hero", icon: LayoutTemplate },
  { type: "text", label: "Texto enriquecido", icon: Type },
  { type: "columns", label: "Columnas", icon: Columns3 },
  { type: "image", label: "Imagen", icon: ImageIcon },
  { type: "people", label: "Personas / ponentes", icon: Users },
  { type: "form", label: "Formulario", icon: FileText },
  { type: "button", label: "Botón / CTA", icon: MousePointerClick },
  { type: "video", label: "Video", icon: Video },
  { type: "countdown", label: "Cuenta regresiva", icon: Clock },
  { type: "divider", label: "Divisor / espacio", icon: Minus },
  { type: "share", label: "Barra para compartir", icon: Share2 },
  { type: "footer", label: "Pie de página", icon: PanelBottom },
  { type: "html", label: "HTML (importado)", icon: Code },
];
const LABEL = Object.fromEntries(BLOCKS.map((b) => [b.type, b.label])) as Record<BlockType, string>;

function defaults(type: BlockType): Block {
  const id = newId();
  switch (type) {
    case "hero": return { id, type, title: "Título principal", subtitle: "Subtítulo de la landing", buttonText: "Inscríbete", buttonUrl: "#registro", overlay: 35 };
    case "text": return { id, type, html: "<p>Escribe aquí tu texto.</p>" };
    case "columns": return { id, type, items: [
      { title: "¿A quién va dirigido?", text: "<p>…</p>" }, { title: "¿Cuándo?", text: "<p>…</p>" }, { title: "¿Dónde?", text: "<p>…</p>" }] };
    case "image": return { id, type, url: "", alt: "", width: 100 };
    case "people": return { id, type, title: "Nuestros ponentes", items: [{ name: "Nombre", description: "Cargo / descripción", photo: "" }] };
    case "form": return { id, type, formId: null, title: "Regístrate" };
    case "button": return { id, type, text: "Quiero más información", url: "https://", style: { align: "center" } };
    case "video": return { id, type, url: "" };
    case "countdown": return { id, type, date: new Date(Date.now() + 14 * 86400_000).toISOString().slice(0, 16), title: "Faltan" , style: { align: "center" } };
    case "divider": return { id, type, kind: "line", height: 32 };
    case "share": return { id, type, title: "Compártelo", style: { align: "center" } };
    case "footer": return { id, type, html: "<p>© SACS Consultores · Todos los derechos reservados</p>", style: { align: "center", bg: "#1f2937", color: "#ffffff", padding: 24 } };
    case "html": return { id, type, html: "" };
  }
}

/** Quita claves vacías opcionales que el servidor no aceptaría (p. ej. "" en colores). */
function cleanBlock(b: Block): Block {
  const out: Block = { ...b };
  if (out.style) {
    const st = Object.fromEntries(Object.entries(out.style).filter(([, v]) => v !== "" && v != null));
    if (Object.keys(st).length) out.style = st; else delete out.style;
  }
  for (const k of ["image", "buttonText", "buttonUrl", "link", "alt", "title", "subtitle", "buttonColor", "buttonTextColor"]) if (out[k] === "") delete out[k];
  if (out.type === "countdown" && typeof out["date"] === "string" && out["date"]) {
    const d = new Date(out["date"] as string); if (!Number.isNaN(d.getTime())) out["date"] = d.toISOString();
  }
  if (out.type === "people" && Array.isArray(out["items"])) out["items"] = (out["items"] as Array<Record<string, string>>).map((p) => Object.fromEntries(Object.entries(p).filter(([k, v]) => k === "name" || v !== "")));
  return out;
}
const cleanDesign = (d: Design): Design => {
  const g = Object.fromEntries(Object.entries(d.global ?? {}).filter(([, v]) => v !== "" && v != null));
  return { global: g, blocks: d.blocks.map(cleanBlock) };
};
const cleanSeo = (s: Seo): Seo => Object.fromEntries(Object.entries(s).filter(([, v]) => v !== "" && v != null)) as Seo;

// ────────── Controles reutilizables ──────────
function ColorInput({ label, value, onChange }: { label: string; value: string | undefined; onChange: (v: string | undefined) => void }): JSX.Element {
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#ffffff"} onChange={(e) => onChange(e.target.value)} className="w-9 h-9 rounded border border-line bg-surface p-0.5 cursor-pointer" aria-label={label} />
        <Input value={value ?? ""} onChange={(e) => onChange(e.target.value || undefined)} placeholder="Automático" maxLength={9} />
      </div>
    </Field>
  );
}

/** Imagen por URL (https) o elegida/subida en la biblioteca de medios. */
function ImageInput({ label, value, onChange }: { label: string; value: string | undefined; onChange: (v: string) => void }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <Field label={label}>
      <div className="flex gap-2">
        <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="https://…" maxLength={1000} />
        <Button small type="button" onClick={() => setOpen((v) => !v)}>Biblioteca</Button>
      </div>
      {open && <div className="mt-2"><MediaPicker open={open} onClose={() => setOpen(false)} onSelect={(m) => { onChange(m.url); setOpen(false); }} /></div>}
    </Field>
  );
}

function Row({ children }: { children: ReactNode }): JSX.Element { return <div className="grid grid-cols-2 gap-2">{children}</div>; }

// ────────── Propiedades por tipo de bloque ──────────
function BlockProps({ b, set, forms }: { b: Block; set: (patch: Partial<Block>) => void; forms: Array<{ id: string; name: string; status: string }> }): JSX.Element {
  const str = (k: string): string => (typeof b[k] === "string" ? (b[k] as string) : "");
  const num = (k: string, d: number): number => (typeof b[k] === "number" ? (b[k] as number) : d);
  const st = b.style ?? {};
  const setStyle = (p: Partial<NonNullable<Block["style"]>>): void => set({ style: { ...st, ...p } });
  let body: JSX.Element | null = null;
  switch (b.type) {
    case "hero":
      body = <>
        <ImageInput label="Imagen de fondo" value={str("image")} onChange={(v) => set({ image: v })} />
        <Field label="Título"><Input value={str("title")} maxLength={300} onChange={(e) => set({ title: e.target.value })} /></Field>
        <Field label="Subtítulo"><Textarea rows={2} value={str("subtitle")} maxLength={1000} onChange={(e) => set({ subtitle: e.target.value })} /></Field>
        <Row>
          <Field label="Texto del botón"><Input value={str("buttonText")} maxLength={80} onChange={(e) => set({ buttonText: e.target.value })} /></Field>
          <Field label="Enlace del botón"><Input value={str("buttonUrl")} maxLength={1000} onChange={(e) => set({ buttonUrl: e.target.value })} placeholder="https:// o #ancla" /></Field>
        </Row>
        <Row>
          <Field label={`Oscurecer imagen (${num("overlay", 35)}%)`}><input type="range" min={0} max={90} value={num("overlay", 35)} onChange={(e) => set({ overlay: Number(e.target.value) })} className="w-full" /></Field>
          <Field label="Alto mínimo (px)"><Input type="number" min={120} max={900} value={num("minHeight", 380)} onChange={(e) => set({ minHeight: Math.max(120, Math.min(900, Number(e.target.value) || 380)) })} /></Field>
        </Row>
      </>;
      break;
    case "text": case "footer": case "html":
      body = <Field label={b.type === "html" ? "HTML (se sanea al guardar: sin scripts)" : "Contenido (HTML básico: p, b, i, a, ul, h2…)"}>
        <Textarea rows={b.type === "html" ? 16 : 8} className="font-mono text-xs" value={str("html")} onChange={(e) => set({ html: e.target.value })} />
      </Field>;
      break;
    case "columns": {
      const items = (b["items"] as Array<{ title: string; text: string }>) ?? [];
      body = <>
        <Field label="Número de columnas">
          <Select value={items.length} onChange={(e) => {
            const n = Number(e.target.value);
            set({ items: n > items.length ? [...items, ...Array.from({ length: n - items.length }, () => ({ title: "Título", text: "<p>…</p>" }))] : items.slice(0, n) });
          }}>{[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}</Select>
        </Field>
        {items.map((it, i) => (
          <div key={i} className="rounded-md border border-line p-2 space-y-2">
            <Input value={it.title} maxLength={200} placeholder={`Título columna ${i + 1}`} onChange={(e) => set({ items: items.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) })} />
            <Textarea rows={3} className="font-mono text-xs" value={it.text} onChange={(e) => set({ items: items.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) })} />
          </div>
        ))}
      </>;
      break;
    }
    case "image":
      body = <>
        <ImageInput label="Imagen" value={str("url")} onChange={(v) => set({ url: v })} />
        <Field label="Texto alternativo"><Input value={str("alt")} maxLength={200} onChange={(e) => set({ alt: e.target.value })} /></Field>
        <Field label="Enlace (opcional)"><Input value={str("link")} maxLength={1000} onChange={(e) => set({ link: e.target.value })} placeholder="https://" /></Field>
        <Field label={`Ancho (${num("width", 100)}%)`}><input type="range" min={10} max={100} value={num("width", 100)} onChange={(e) => set({ width: Number(e.target.value) })} className="w-full" /></Field>
      </>;
      break;
    case "people": {
      const items = (b["items"] as Array<{ name: string; description?: string; photo?: string }>) ?? [];
      const upd = (i: number, p: Partial<{ name: string; description: string; photo: string }>): void => set({ items: items.map((x, j) => (j === i ? { ...x, ...p } : x)) });
      body = <>
        <Field label="Título de la sección"><Input value={str("title")} maxLength={200} onChange={(e) => set({ title: e.target.value })} /></Field>
        {items.map((p, i) => (
          <div key={i} className="rounded-md border border-line p-2 space-y-2">
            <div className="flex gap-2">
              <Input value={p.name} maxLength={120} placeholder="Nombre" onChange={(e) => upd(i, { name: e.target.value })} />
              <button type="button" className="btn-ghost btn-sm text-danger" aria-label="Quitar persona" onClick={() => set({ items: items.filter((_, j) => j !== i) })}><Trash2 size={14} /></button>
            </div>
            <ImageInput label="Foto" value={p.photo} onChange={(v) => upd(i, { photo: v })} />
            <Textarea rows={2} value={p.description ?? ""} maxLength={1000} placeholder="Descripción" onChange={(e) => upd(i, { description: e.target.value })} />
          </div>
        ))}
        {items.length < 24 && <Button small type="button" onClick={() => set({ items: [...items, { name: "Nombre", description: "", photo: "" }] })}><Plus size={14} /> Añadir persona</Button>}
      </>;
      break;
    }
    case "form":
      body = <>
        <Field label="Título"><Input value={str("title")} maxLength={200} onChange={(e) => set({ title: e.target.value })} /></Field>
        <Field label="Formulario de Tiger" hint="Se incrusta con el widget de Formularios. Solo los publicados se muestran.">
          <Select value={str("formId")} onChange={(e) => set({ formId: e.target.value || null })}>
            <option value="">— Elige un formulario —</option>
            {forms.map((f) => <option key={f.id} value={f.id}>{f.name}{f.status !== "published" ? " (borrador)" : ""}</option>)}
          </Select>
        </Field>
        {!forms.length && <p className="text-xs text-ink-muted">No hay formularios. Créalos en Marketing → Formularios.</p>}
      </>;
      break;
    case "button":
      body = <>
        <Row>
          <Field label="Texto"><Input value={str("text")} maxLength={80} onChange={(e) => set({ text: e.target.value })} /></Field>
          <Field label="Enlace"><Input value={str("url")} maxLength={1000} onChange={(e) => set({ url: e.target.value })} placeholder="https://, mailto:, tel:, #" /></Field>
        </Row>
        <Row>
          <ColorInput label="Color del botón" value={str("buttonColor") || undefined} onChange={(v) => set({ buttonColor: v ?? "" })} />
          <ColorInput label="Texto del botón" value={str("buttonTextColor") || undefined} onChange={(v) => set({ buttonTextColor: v ?? "" })} />
        </Row>
      </>;
      break;
    case "video":
      body = <Field label="URL de YouTube o Vimeo" hint="Se muestra la miniatura; al hacer clic se abre el video.">
        <Input value={str("url")} maxLength={500} onChange={(e) => set({ url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" />
      </Field>;
      break;
    case "countdown": {
      const v = str("date"); const local = v && !Number.isNaN(Date.parse(v)) ? new Date(new Date(v).getTime() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 16) : "";
      body = <>
        <Field label="Título"><Input value={str("title")} maxLength={200} onChange={(e) => set({ title: e.target.value })} /></Field>
        <Field label="Fecha y hora del evento"><Input type="datetime-local" value={local} onChange={(e) => set({ date: e.target.value ? new Date(e.target.value).toISOString() : "" })} /></Field>
      </>;
      break;
    }
    case "divider":
      body = <Row>
        <Field label="Tipo"><Select value={str("kind") || "line"} onChange={(e) => set({ kind: e.target.value })}><option value="line">Línea</option><option value="space">Espacio</option></Select></Field>
        <Field label="Alto (px)"><Input type="number" min={0} max={200} value={num("height", 32)} onChange={(e) => set({ height: Math.max(0, Math.min(200, Number(e.target.value) || 0)) })} /></Field>
      </Row>;
      break;
    case "share":
      body = <Field label="Título" hint="Facebook, X, LinkedIn, WhatsApp y correo (enlaces, sin scripts)."><Input value={str("title")} maxLength={120} onChange={(e) => set({ title: e.target.value })} /></Field>;
      break;
  }
  return (
    <div className="space-y-3">
      {body}
      {b.type !== "html" && (
        <div className="border-t border-line pt-3 space-y-2">
          <div className="dt-label">Estilo del bloque</div>
          <Row>
            <ColorInput label="Fondo" value={st.bg} onChange={(v) => setStyle({ bg: v })} />
            <ColorInput label="Texto" value={st.color} onChange={(v) => setStyle({ color: v })} />
          </Row>
          <Row>
            <Field label="Relleno vertical (px)"><Input type="number" min={0} max={160} value={st.padding ?? ""} placeholder="32" onChange={(e) => setStyle({ padding: e.target.value === "" ? undefined : Math.max(0, Math.min(160, Number(e.target.value))) })} /></Field>
            <Field label="Alineación">
              <div className="flex gap-1">
                {([["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight]] as const).map(([a, I]) => (
                  <button key={a} type="button" aria-label={a} onClick={() => setStyle({ align: st.align === a ? undefined : a })}
                    className={`btn-sm ${st.align === a ? "btn-primary" : "btn-secondary"}`}><I size={14} /></button>
                ))}
              </div>
            </Field>
          </Row>
        </div>
      )}
    </div>
  );
}

// ────────── Editor ──────────
export function LandingEditor(): JSX.Element {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const q = useQuery<LandingDetailData>({ queryKey: ["mkt-landing", id], queryFn: () => api.get(`/mkt/landings/${id}`) });
  const forms = useQuery<{ rows: Array<{ id: string; name: string; status: string }> }>({ queryKey: ["mkt-landing-forms"], queryFn: () => api.get("/mkt/landings/form-options") });

  const [design, setDesign] = useState<Design | null>(null);
  const [seo, setSeo] = useState<Seo>({});
  const [name, setName] = useState("");
  const [sel, setSel] = useState<string | null>(null);
  const [panel, setPanel] = useState<"blocks" | "global" | "seo">("blocks");
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [dirty, setDirty] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);

  useEffect(() => {
    if (!q.data || design) return;
    const d = q.data.design?.blocks ? q.data.design : { blocks: [] };
    // Página importada sin bloques: su HTML se edita como un bloque HTML (se pueden añadir más).
    const blocks = d.blocks.length || !q.data.html ? d.blocks : [{ id: newId(), type: "html" as const, html: q.data.html }];
    setDesign({ global: d.global ?? {}, blocks });
    setSeo(q.data.seo ?? {}); setName(q.data.name);
  }, [q.data, design]);

  const payload = useMemo(() => (design ? { design: cleanDesign(design), seo: cleanSeo(seo) } : null), [design, seo]);

  // Vista previa renderizada en servidor (debounce).
  useEffect(() => {
    if (!payload) return;
    const t = setTimeout(() => {
      api.post<{ url: string }>("/mkt/landings/preview", { id, name: name || "Vista previa", ...payload })
        .then((r) => { setPreviewUrl(r.url); setPreviewErr(null); })
        .catch((e: unknown) => setPreviewErr(e instanceof ApiError ? e.message : "No se pudo generar la vista previa."));
    }, 600);
    return () => clearTimeout(t);
  }, [payload, id, name]);

  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent): void => { e.preventDefault(); };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const save = useMutation({
    mutationFn: () => api.patch(`/mkt/landings/${id}`, { name: name.trim() || q.data?.name, ...payload }),
    onSuccess: () => { setDirty(false); toast.success("Landing guardada."); void qc.invalidateQueries({ queryKey: ["mkt-landing", id] }); void qc.invalidateQueries({ queryKey: ["mkt-landings"] }); },
    onError: (e: unknown) => toast.error(e instanceof ApiError ? e.message : "No se pudo guardar."),
  });

  if (q.isLoading || !design) return q.isError ? <ErrorState message="No se pudo cargar la landing." /> : <Loading />;

  const upd = (fn: (d: Design) => Design): void => { setDesign((d) => (d ? fn(d) : d)); setDirty(true); };
  const blocks = design.blocks;
  const selected = blocks.find((b) => b.id === sel) ?? null;
  const setBlock = (bid: string, patch: Partial<Block>): void => upd((d) => ({ ...d, blocks: d.blocks.map((b) => (b.id === bid ? { ...b, ...patch } as Block : b)) }));
  const move = (i: number, dir: -1 | 1): void => upd((d) => {
    const arr = [...d.blocks]; const j = i + dir; if (j < 0 || j >= arr.length) return d;
    [arr[i], arr[j]] = [arr[j]!, arr[i]!]; return { ...d, blocks: arr };
  });
  const add = (t: BlockType): void => {
    const nb = defaults(t);
    const idx = selected ? blocks.findIndex((b) => b.id === selected.id) + 1 : blocks.length;
    upd((d) => ({ ...d, blocks: [...d.blocks.slice(0, idx), nb, ...d.blocks.slice(idx)] }));
    setSel(nb.id);
  };
  const g = design.global ?? {};
  const setG = (p: Partial<NonNullable<Design["global"]>>): void => upd((d) => ({ ...d, global: { ...d.global, ...p } }));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Link to=".." relative="path" className="btn-ghost btn-sm" aria-label="Volver"><ArrowLeft size={16} /></Link>
          <Input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} maxLength={160} className="max-w-md font-medium" aria-label="Nombre de la landing" />
          {dirty && <span className="text-xs text-warning whitespace-nowrap">Sin guardar</span>}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-line overflow-hidden">
            <button type="button" className={`px-2.5 py-1.5 ${device === "desktop" ? "bg-primary-soft text-primary" : "text-ink-secondary"}`} onClick={() => setDevice("desktop")} aria-label="Escritorio"><Monitor size={16} /></button>
            <button type="button" className={`px-2.5 py-1.5 ${device === "mobile" ? "bg-primary-soft text-primary" : "text-ink-secondary"}`} onClick={() => setDevice("mobile")} aria-label="Móvil"><Smartphone size={16} /></button>
          </div>
          <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}><Save size={16} /> Guardar</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-3">
        <Card className="p-3 space-y-3 xl:max-h-[calc(100vh-220px)] xl:overflow-y-auto scrollbar-thin">
          <div className="flex gap-1 border-b border-line -mx-3 px-3">
            {([["blocks", "Bloques"], ["global", "Diseño"], ["seo", "SEO"]] as const).map(([k, l]) => (
              <button key={k} type="button" onClick={() => setPanel(k)} className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${panel === k ? "border-primary text-primary font-medium" : "border-transparent text-ink-secondary"}`}>{l}</button>
            ))}
          </div>

          {panel === "blocks" && (
            <>
              <div className="space-y-1">
                {blocks.length === 0 && <p className="text-sm text-ink-muted py-2">Añade el primer bloque.</p>}
                {blocks.map((b, i) => {
                  const Icon = BLOCKS.find((x) => x.type === b.type)?.icon ?? Type;
                  const sub = typeof b["title"] === "string" ? (b["title"] as string) : typeof b["text"] === "string" ? (b["text"] as string) : "";
                  return (
                    <div key={b.id} className={`flex items-center gap-2 rounded-md border px-2 py-1.5 cursor-pointer ${sel === b.id ? "border-primary bg-primary-soft" : "border-line hover:bg-surface-hover"}`} onClick={() => setSel(sel === b.id ? null : b.id)}>
                      <Icon size={15} strokeWidth={1.75} className="text-ink-secondary shrink-0" />
                      <div className="min-w-0 flex-1"><div className="text-sm text-ink truncate">{LABEL[b.type]}</div>{sub && <div className="text-xs text-ink-muted truncate">{sub}</div>}</div>
                      <div className="flex shrink-0" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="btn-ghost btn-sm" aria-label="Subir" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp size={13} /></button>
                        <button type="button" className="btn-ghost btn-sm" aria-label="Bajar" disabled={i === blocks.length - 1} onClick={() => move(i, 1)}><ArrowDown size={13} /></button>
                        <button type="button" className="btn-ghost btn-sm" aria-label="Duplicar" onClick={() => upd((d) => ({ ...d, blocks: [...d.blocks.slice(0, i + 1), { ...structuredClone(b), id: newId() }, ...d.blocks.slice(i + 1)] }))}><Copy size={13} /></button>
                        <button type="button" className="btn-ghost btn-sm text-danger" aria-label="Eliminar" onClick={() => { upd((d) => ({ ...d, blocks: d.blocks.filter((x) => x.id !== b.id) })); if (sel === b.id) setSel(null); }}><Trash2 size={13} /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {selected && (
                <div className="rounded-md border border-primary/40 p-3 space-y-2">
                  <div className="text-sm font-medium text-ink">{LABEL[selected.type]}</div>
                  <BlockProps b={selected} set={(p) => setBlock(selected.id, p)} forms={forms.data?.rows ?? []} />
                </div>
              )}
              <div>
                <div className="dt-label mb-1.5">Añadir bloque{selected ? " (debajo del seleccionado)" : ""}</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {BLOCKS.filter((b) => b.type !== "html").map((b) => (
                    <button key={b.type} type="button" onClick={() => add(b.type)} className="flex items-center gap-2 rounded-md border border-line px-2 py-1.5 text-xs text-ink-secondary hover:bg-surface-hover hover:text-ink text-left">
                      <b.icon size={14} strokeWidth={1.75} /> {b.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {panel === "global" && (
            <div className="space-y-3">
              <Field label="Fuente"><Select value={g.font ?? "Sistema"} onChange={(e) => setG({ font: e.target.value })}>{FONTS.map((f) => <option key={f}>{f}</option>)}</Select></Field>
              <ColorInput label="Color primario (botones, títulos de columnas)" value={g.primary} onChange={(v) => setG({ primary: v })} />
              <Row>
                <ColorInput label="Fondo de la página" value={g.bg} onChange={(v) => setG({ bg: v })} />
                <ColorInput label="Color de texto" value={g.text} onChange={(v) => setG({ text: v })} />
              </Row>
              <Field label="Ancho máximo del contenido (px)"><Input type="number" min={480} max={1600} value={g.maxWidth ?? 1100} onChange={(e) => setG({ maxWidth: Math.max(480, Math.min(1600, Number(e.target.value) || 1100)) })} /></Field>
            </div>
          )}

          {panel === "seo" && (
            <div className="space-y-3">
              <Field label="Título (pestaña y buscadores)"><Input value={seo.title ?? ""} maxLength={160} onChange={(e) => { setSeo({ ...seo, title: e.target.value }); setDirty(true); }} /></Field>
              <Field label="Descripción"><Textarea rows={3} value={seo.description ?? ""} maxLength={400} onChange={(e) => { setSeo({ ...seo, description: e.target.value }); setDirty(true); }} /></Field>
              <ImageInput label="Imagen para redes (OG)" value={seo.image} onChange={(v) => { setSeo({ ...seo, image: v }); setDirty(true); }} />
              <ImageInput label="Favicon" value={seo.favicon} onChange={(v) => { setSeo({ ...seo, favicon: v }); setDirty(true); }} />
            </div>
          )}
        </Card>

        <Card className="p-3 bg-canvas">
          <div className="flex items-center justify-between mb-2 text-xs text-ink-muted">
            <span>Vista previa · {device === "desktop" ? "Escritorio" : "Móvil (390 px)"}</span>
            {previewErr ? <span className="text-danger">{previewErr}</span> : <span className="inline-flex items-center gap-1"><RefreshCw size={12} /> se actualiza al editar</span>}
          </div>
          <div className="flex justify-center">
            {previewUrl && (
              <iframe key={previewUrl} src={previewUrl} title="Vista previa de la landing" sandbox="allow-scripts allow-popups allow-forms"
                className="bg-white border border-line rounded-md shadow-sm transition-all"
                style={{ width: device === "desktop" ? "100%" : 390, height: "calc(100vh - 260px)", minHeight: 480 }} />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
